import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { TruckForm } from './TruckForm';

import { useCreateTruckMutation } from '~/modules/transportation/hooks/mutations/useCreateTruckMutation';
import { useUpdateTruckMutation } from '~/modules/transportation/hooks/mutations/useUpdateTruckMutation';
import {
  $resetTruckForm,
  $truckForm,
  $updateTruckForm,
} from '~/modules/transportation/state/truckForm.localState';
import { TruckFormState } from '~/modules/transportation/types';

interface TruckDialogProps {
  truck?: TruckFormState & { id: string };
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const TruckDialog = ({
  truck,
  isOpen,
  onClose,
  onSuccess,
}: TruckDialogProps) => {
  const toast = useToast();
  const t = useTranslations();
  const { mutateAsync: createTruck, isPending: isCreating } =
    useCreateTruckMutation();
  const { mutateAsync: updateTruck, isPending: isUpdating } =
    useUpdateTruckMutation();
  const formData = useAtomValue($truckForm);
  const resetForm = useSetAtom($resetTruckForm);
  const updateForm = useSetAtom($updateTruckForm);

  React.useEffect(() => {
    if (isOpen && truck) {
      updateForm(truck);
    }
  }, [isOpen, truck, updateForm]);

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    try {
      if (truck) {
        await updateTruck({
          id: truck.id,
          ...formData,
        });
        toast.success(t.trucksView.messages.updateSuccess);
      } else {
        await createTruck(formData);
        toast.success(t.trucksView.messages.createSuccess);
      }
      onSuccess?.();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.trucksView.messages.error,
      );
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@TruckDialog"
    >
      <DialogContent slot="title" position="center">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {truck
              ? t.trucksView.dialog.title.edit
              : t.trucksView.dialog.title.create}
          </div>
        </DialogHeader>

        <TruckForm isEditing={!!truck} />

        <DialogFooter>
          <Button
            color="failure"
            variant="outline"
            isPending={isCreating || isUpdating}
            onPress={handleClose}
          >
            {t.trucksView.dialog.actions.cancel}
          </Button>
          <Button
            color="primary"
            variant="solid"
            isPending={isCreating || isUpdating}
            isLoading={isCreating || isUpdating}
            onPress={handleSubmit}
          >
            {truck
              ? t.trucksView.dialog.actions.save
              : t.trucksView.dialog.actions.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
